<template>
  <PageTitle hide-margin-left>Метрики</PageTitle>

  <div class="wrapper">
    <div class="filters-card">
      <div class="filters-container">
        <CustomSelect
          v-for="filter in filterConfigs"
          :key="filter.key"
          :options="filter.options"
          :value="filters[filter.key]"
          :onChange="value => onFiltersChange(filter.key, value)"
          :optionLabelKey="filter.labelKey"
          :optionValueKey="filter.valueKey"
          :placeholder="filter.placeholder"
          :fullWidth="true"
        />
      </div>
    </div>

    <div class="content">
      <div v-if="isFetching" class="loading-bar">
        <q-linear-progress indeterminate />
      </div>

      <div v-if="projectDatePercent?.length" class="projects-section">
        <h2 class="section-title">Информация о проектах</h2>
        <div class="projects-grid">
          <ProjectProgressCard
            v-for="project in projectDatePercent"
            :key="project.project_id"
            :project="project"
            @click="onProjectIdClick"
          />
        </div>
      </div>
      <div v-if="!chartData?.length" class="no-data">
        <div class="no-data-card">
          <div class="no-data-text">
            {{ isFetching ? 'Загрузка данных...' : 'Нет данных для отображения' }}
          </div>
        </div>
      </div>

      <!-- Подсказка для выбора обязательных фильтров -->
      <!-- <div
        v-if="chartData?.length && !hasRequiredFilters && showHint"
        class="required-filters-hint"
      >
        <div class="hint-card">
          <div class="hint-content">
            <div class="hint-icon">💡</div>
            <div class="hint-text">
              Для отображения таблицы с данными при клике на сектор диаграммы,
              пожалуйста, выберите:<br />
              <strong>ЖК</strong> и <strong>Тип проекта</strong>
            </div>
          </div>
          <q-btn
            flat
            round
            dense
            icon="close"
            size="sm"
            color="grey-7"
            @click="hideHint"
            class="hint-close-btn"
          />
        </div>
      </div> -->
      <div class="chart-container">
        <div v-if="!!chartData?.length" class="charts-grid">
          <div v-for="item in chartData" :key="item.code" class="chart-card">
            <div class="tw-h-[5px] tw-w-full tw-mb-1">
              <q-linear-progress
                v-if="isMetricsFetching[item.code]"
                indeterminate
                rounded
              />
            </div>
            <div class="chart-header">
              <div class="tw-flex tw-items-center tw-gap-2">
                <div class="tw-w-[25px] tw-h-[25px] tw-flex tw-justify-center tw-items-center tw-bg-[#e8eaee] tw-rounded-md">
                  <q-icon
                    :name="getMetricIcon(item.code)"
                    size="16px"
                    color="blue-grey-8"
                    class="chart-icon"
                  />
                </div>
                <p class="chart-title">{{ item.title }}</p>
              </div>
              <span v-if="item.code !== 'WORK_PAYMENT'" class="total-count">
                {{ getTotalCount(item) }}
                <q-tooltip>Общее количество</q-tooltip>
              </span>
            </div>
            <Pie
              :data="item"
              :options="chartOptions"
              :width="300"
              :height="290"
            />
          </div>
        </div>
      </div>

      <div v-if="showTable" class="table-container">
        <div v-if="hasAnyFilters" class="table-filters">
          <div
            v-if="Object.keys(availableFilters).length"
            class="table-filters__row"
          >
            <CustomSelect
              v-for="(opts, colName) in availableFilters"
              :key="colName"
              :options="opts"
              :value="columnFilters[colName] ?? []"
              :onChange="value => onColumnFilterChange(colName, value)"
              optionLabelKey="label"
              optionValueKey="value"
              :placeholder="getColumnLabel(colName)"
              :fullWidth="true"
              :multiple="true"
            />
          </div>
          <template v-if="dateColumns.length">
            <template v-for="colName in dateColumns" :key="`date_${colName}`">
              <CustomDate
                :label="`${getColumnLabel(colName)} с`"
                :value="dateFilters[colName]?.from || ''"
                @change_date="val => onDateFilterChange(colName, 'from', val)"
                :fullWidth="true"
              />
              <CustomDate
                :label="`${getColumnLabel(colName)} по`"
                :value="dateFilters[colName]?.to || ''"
                @change_date="val => onDateFilterChange(colName, 'to', val)"
                :fullWidth="true"
              />
            </template>
          </template>
        </div>
        <CustomTable
          v-if="!!chartData?.length"
          :title="selectedSegment?.label"
          :columns="sortedColumns"
          :rowsData="filteredTableRows"
          :loading="isTableFetching"
          :custom-body="true"
          :small="true"
          :show-search="false"
          :sticky-header="false"
        >
          <template #customBody="props">
            <q-tr>
              <custom-table-cell
                v-for="(value, key, index) in props.row"
                :key="key || index"
                align="center"
                vertical-align="center"
              >
                <span
                  :class="{'clickable-id': isProjectIdKey(key)}"
                  @click="isProjectIdKey(key) ? onProjectIdClick(value) : null"
                >
                  {{ value }}
                  <q-tooltip v-if="isProjectIdKey(key)">Перейти на страницу проекта</q-tooltip>
                </span>
              </custom-table-cell>
            </q-tr>
          </template>
        </CustomTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell.vue';
import PageTitle from '@/components/common/PageTitle.vue';
import ProjectProgressCard from '@/components/common/ProjectProgressCard.vue';
import CustomSelect from '@/components/form/CustomSelect';
import CustomDate from '@/components/form/CustomDate';
import { numberWithCommas, showErrorToast, showInfoToast } from '@/services';
import { useQuery } from '@tanstack/vue-query';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { onMounted, ref, watch, computed } from 'vue';
import { Pie } from 'vue-chartjs';
import { getCommonResidents, getMetricData, getMetrics, getOnTimeMetricLabel, getProjectHeads, getProjectSectionForemen, metricLabels } from './services';
import { getProjectTypes } from '../BuildingSettings/services';
import { getFloorProjectInfo } from '../ResidentialSettings/services';
import { PAGES } from '@/constants';
import { useRoute, useRouter } from 'vue-router';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels)

const HINT_HIDDEN_KEY = 'mainPageHintHidden';

const route = useRoute()
const router = useRouter()
// Load hint state from localStorage
const loadHintState = () => {
  const stored = localStorage.getItem(HINT_HIDDEN_KEY);
  showHint.value = stored !== 'true';
};

// Hide hint and save to localStorage
const hideHint = () => {
  showHint.value = false;
  localStorage.setItem(HINT_HIDDEN_KEY, 'true');
};

// Safely parse URL parameters
const parseUrlFilters = () => {
  const query = route.query;
  const urlFilters = {};

  // Only process valid filter keys
  const validFilterKeys = ['resident_id', 'project_type_id', 'section_foreman_id', 'project_head_id'];

  validFilterKeys.forEach(key => {
    if (query[key]) {
      // Sanitize input - only allow alphanumeric and underscore
      const value = String(query[key]).replace(/[^a-zA-Z0-9_-]/g, '');
      if (value) {
        urlFilters[key] = value;
      }
    }
  });

  return urlFilters;
};

// Update URL with current filters
const updateUrlFilters = () => {
  const query = { ...route.query };

  // Update or remove filter parameters
  Object.keys(filters.value).forEach(key => {
    if (filters.value[key]) {
      query[key] = filters.value[key];
    } else {
      delete query[key];
    }
  });

  // Only update if query actually changed
  const currentQuery = JSON.stringify(route.query);
  const newQuery = JSON.stringify(query);

  if (currentQuery !== newQuery) {
    router.replace({ query });
  }
};
const isFetching = ref(false)
const filters = ref({
  resident_id: '',
  project_type_id: '',
  section_foreman_id: '',
  project_head_id: '',
})

const data = ref(null)
const projectDatePercent = ref([])
const tableData = ref([])
const isTableFetching = ref(false)
const fetchingProjectInfo = ref(false)
const isMetricsFetching = ref({})
const showTable = ref(false)
const selectedSegment = ref(null)
const columnFilters = ref({})
const dateFilters = ref({})
const showHint = ref(true)

// Queries
const { data: residentialOptions } = useQuery({
  queryKey: ["residentialOptions"],
  queryFn: getCommonResidents,
});

const { data: projectHeads } = useQuery({
  queryKey: ["workSets"],
  queryFn: getProjectHeads,
});

const { data: projectTypes } = useQuery({
  queryKey: ["projectTypes"],
  queryFn: getProjectTypes,
});

const { data: sectionForemen } = useQuery({
  queryKey: ["sectionForemen"],
  queryFn: getProjectSectionForemen,
});

// Конфигурация фильтров
const filterConfigs = computed(() => [
  {
    key: 'resident_id',
    options: residentialOptions.value,
    labelKey: 'resident_name',
    valueKey: 'resident_id',
    placeholder: 'Выберите ЖК'
  },
  {
    key: 'project_type_id',
    options: projectTypes.value,
    labelKey: 'project_type_name',
    valueKey: 'project_type_id',
    placeholder: 'Тип проекта'
  },
  {
    key: 'project_head_id',
    options: projectHeads.value,
    labelKey: 'fio',
    valueKey: 'employee_id',
    placeholder: 'Руководитель проекта'
  },
  {
    key: 'section_foreman_id',
    options: sectionForemen.value,
    labelKey: 'fio',
    valueKey: 'employee_id',
    placeholder: 'Начальник участка'
  }
])

// Chart options
const chartOptions = ref({
  responsive: false,
  maintainAspectRatio: false,
  onHover: (event, activeElements) => {
    event.native.target.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default'
  },
  style: {
    padding: 15,
  },
  layout: {
    padding: {
      top: 15,
      bottom: 15,
      left: 15,
      right: 15
    }
  },
  elements: {
    arc: {
      hoverOffset: 15, // выдвижение сектора при наведении
      borderWidth: 2,
      hoverBorderWidth: 3,
      shadowOffsetX: 3,
      shadowOffsetY: 3,
      shadowBlur: 10,
      shadowColor: 'rgba(0, 0, 0, 0.2)'
    },
  },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const currentData = data.value?.find(item => item.metric_code === context.dataset.code)?.data
          const statusCnt = currentData?.[context.dataIndex]?.status_cnt || 0
          const percent = context.parsed

          return context.dataset.code === 'WORK_PAYMENT'
            ? `${percent}% (${numberWithCommas(statusCnt)})`
            : `${percent}% (${statusCnt})`
        }
      }
    },
    datalabels: {
      display: (context) => {
        const currentData = data.value?.find(item => item.metric_code === context.dataset.code)?.data
        const percent = currentData?.[context.dataIndex]?.status_percent !== undefined
          ? Number(currentData[context.dataIndex]?.status_percent)
          : context.parsed
        return percent > 5
      },
      color: '#404040',
      font: context => {
        return {
          weight: '600',
          size: 14,
          family: 'Open Sans, sans-serif'
        }
      },
      formatter: (value, context) => {
        const currentData = data.value?.find(item => item.metric_code === context.dataset.code)?.data
        const statusCnt = currentData?.[context.dataIndex]?.status_cnt || 0

        return context.dataset.code === 'WORK_PAYMENT'
          ? `${value}%`
          : `${value}% (${statusCnt})`
      },
      anchor: 'center',
      align: 'center',
      offset: (context) => context.dataIndex % 2 === 0 ? 10 : -10,
      clamp: true,
      rotation: (context) => {
        const currentData = data.value?.find(item => item.metric_code === context.dataset.code)?.data
        const percent = currentData?.[context.dataIndex]?.status_percent !== undefined
          ? Number(currentData[context.dataIndex]?.status_percent)
          : context.parsed
        if (percent <= 10) {
          return -30
        }
        return 0
      }
    }
  },
  onClick: (ctx, elements) => {
    const code = ctx?.chart?.config._config?.data?.datasets[0]?.code
    if (!code) return

    const currentChartData = chartData.value?.find(item => item.code === code)
    const idx = chartData.value?.findIndex(item => item.code === code)

    if (elements?.length > 0 && data.value) {
      const elementIndex = elements[0].index
      const label = currentChartData.labels[elementIndex]
      const statusCode = data.value[idx]?.data[elementIndex]?.status_code || null

      selectedSegment.value = {
        label: `${currentChartData.title}: ${label}`,
        index: elementIndex,
        status_code: statusCode,
        metric_code: code
      }
      onChartClick(statusCode, code)
    }
  }
})

// Chart data
const chartData = computed(() => {
  if (!data.value?.length) return null

  return data.value.map(item => ({
    title: item.label,
    code: item.metric_code,
    labels: item.data?.map(d =>
      d.status_code === 'ON_TIME'
        ? getOnTimeMetricLabel(item.metric_code)
        : metricLabels[d.status_code]
    ),
    datasets: [{
      backgroundColor: item.data?.map(d => d.status_colour),
      data: item.data?.map(d => d.status_percent),
      code: item.metric_code,
    }]
  }))
})

// Column types для фильтров
const columnTypes = computed(() => {
  const cols = tableData.value?.columns || []
  const rows = tableData.value?.data || []
  const result = { select: {}, date: [] }

  const numericListRegex = /^\s*\d+(\s*,\s*\d+)*\s*$/
  const dateRegex = /^\s*\d{2}\.\d{2}\.\d{4}\s*$/
  const isNumeric = (val) => typeof val === 'number' || (!isNaN(Number(val)) && String(val).trim() !== '')

  cols.forEach(col => {
    const values = []
    const seen = new Set()

    rows.forEach(r => {
      const v = r[col.field]
      if (v === undefined || v === null) return
      const s = String(v).trim()
      if (!s || seen.has(s)) return
      seen.add(s)
      values.push(v)
    })

    if (!values.length) return

    const allNumeric = values.every(isNumeric)
    const hasNumericList = values.some(v => typeof v === 'string' && numericListRegex.test(v))
    const allDates = values.every(v => typeof v === 'string' && dateRegex.test(v))

    if (allNumeric || hasNumericList) return

    if (allDates) {
      result.date.push(col.name)
      return
    }

    result.select[col.name] = values.slice(0, 500).map(v => ({
      label: String(v),
      value: v
    }))
  })

  return result
})

const availableFilters = computed(() => columnTypes.value.select || {})
const dateColumns = computed(() => columnTypes.value.date || [])
const hasAnyFilters = computed(() =>
  Object.keys(availableFilters.value).length > 0 || dateColumns.value.length > 0
)

// Check if required filters are selected
// const hasRequiredFilters = computed(() => {
//   return filters.value.resident_id && filters.value.project_type_id
// })

// Date parsing
const parseDateDMYToKey = (val) => {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(val || '').trim())
  if (!match) return null
  const [, dd, mm, yyyy] = match
  return Number(`${yyyy}${mm}${dd}`)
}

// Filtered rows
const filteredTableRows = computed(() => {
  const rows = tableData.value?.data || []
  const filters = columnFilters.value || {}
  const availableKeys = Object.keys(availableFilters.value || {})

  const activeKeys = Object.keys(filters).filter(k =>
    availableKeys.includes(k) && (
      (Array.isArray(filters[k]) && filters[k].length > 0) ||
      (!Array.isArray(filters[k]) && filters[k] !== '' && filters[k] !== undefined && filters[k] !== null)
    )
  )

  const activeDateKeys = dateColumns.value.filter(k => {
    const range = dateFilters.value?.[k] || {}
    return (range.from && parseDateDMYToKey(range.from)) || (range.to && parseDateDMYToKey(range.to))
  })

  if (activeKeys.length === 0 && activeDateKeys.length === 0) return rows

  return rows.filter(row => {
    const passesSelectFilters = activeKeys.every(k => {
      const filterVal = filters[k]
      const rowVal = row[k]
      return Array.isArray(filterVal)
        ? filterVal.some(v => String(v) === String(rowVal))
        : String(rowVal) === String(filterVal)
    })

    const passesDateFilters = activeDateKeys.every(k => {
      const key = parseDateDMYToKey(row[k])
      if (!key) return false

      const range = dateFilters.value?.[k] || {}
      const fromKey = range.from ? parseDateDMYToKey(range.from) : null
      const toKey = range.to ? parseDateDMYToKey(range.to) : null

      if (fromKey && key < fromKey) return false
      if (toKey && key > toKey) return false
      return true
    })

    return passesSelectFilters && passesDateFilters
  })
})

const sortedColumns = computed(() =>
  (tableData.value?.columns || []).map(col => ({
    ...col,
    sortable: true
  }))
)

// Helper functions
const getColumnLabel = (name) => {
  const col = tableData.value?.columns?.find(c => c.name === name)
  return col?.label || name
}

const getTotalCount = (item) => {
  if (!data.value) return 0

  const currentData = data.value.find(d => d.metric_code === item.code)
  if (!currentData?.data) return 0

  return currentData.data.reduce((sum, status) => sum + (status.status_cnt || 0), 0)
}

// Get appropriate icon for each metric code
const getMetricIcon = (metricCode) => {
  const iconMap = {
    'WORK_PAYMENT': 'work', // Оплата работ
    'OKK': 'check_circle', // ОКК - проверка качества
    'TECHNADZOR': 'engineering', // Технадзор
    'PROVIDER_REQUEST': 'local_shipping', // Запрос поставщику
    'AVR': 'construction', // АВР - аварийно-восстановительные работы
    'PAYMENT': 'account_balance_wallet', // Платежи
    'AGREEMENT': 'description' // Договоры
  }
  return iconMap[metricCode] || 'assessment' // Иконка по умолчанию
}

const onFiltersChange = (key, value) => {
  filters.value[key] = value
}

const onColumnFilterChange = (key, value) => {
  const next = Array.isArray(value)
    ? value
    : (value === '' || value === undefined || value === null ? [] : [value])
  columnFilters.value = { ...columnFilters.value, [key]: next }
}

const onDateFilterChange = (key, part, value) => {
  const current = dateFilters.value[key] || {}
  dateFilters.value = { ...dateFilters.value, [key]: { ...current, [part]: value } }
}

// Project ID click
const isProjectIdKey = (key) => {
  if (!key) return false
  const normalized = String(key).toLowerCase().trim()
  return ['project_id', 'projectid', 'id', 'ид', 'id проекта', 'ид проекта'].includes(normalized)
}

const onProjectIdClick = async (value) => {
  if(fetchingProjectInfo.value) return
  fetchingProjectInfo.value = true
  const res = await getFloorProjectInfo(value)
  fetchingProjectInfo.value = false
  if(!res?.data) return
  if (!res?.data?.resident_id) return showErrorToast('Не найден ЖК для проекта')
  try {
    const projectEntranceId = res?.data?.project_entrance_ids?.length ? res?.data?.project_entrance_ids[0] : null
    if(!projectEntranceId) return showErrorToast('Не найден подъезд для проекта')

    // Create URL for the project page
    const url = `${window.location.origin}${PAGES.residentialSettings}?resident_id=${res?.data?.resident_id}&project_type_id=${res?.data?.project_type_id}&project_entrance_id=${projectEntranceId}&project_id=${value}&tab=M__ProjectFormInfoTab`

    // Open in new tab using window.open with _blank target
    // This method works across all browsers and avoids popup blockers
    const newTab = window.open(url, '_blank')

    // Fallback for browsers that might block the popup
    if (!newTab) {
      // Create a temporary anchor element and trigger click programmatically
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  } catch (e) {
    console.error(e)
    showErrorToast('Ошибка при переходе к проекту')
  } finally {
  }
}

// Load metrics
const loadMetrics = async () => {
  if(isFetching.value) return
  try {
    isFetching.value = true
    const res = await getMetrics(filters.value)

    if (res?.data) {
      projectDatePercent.value = res.project_date_percent || []
      data.value = res.data
    } else {
      data.value = null
    }
  } catch (e) {
    console.error('Error loading metrics:', e)
  } finally {
    isFetching.value = false
  }
}

// Chart click handler
const onChartClick = async (status_code, metric_code) => {
  if(isTableFetching.value) return
  // if(!hasRequiredFilters.value) return showInfoToast('Для отображения таблицы с данными, пожалуйста, выберите: ЖК и Тип проекта')
  showTable.value = true
  const params = {
    metric_code,
    status_code,
    ...filters.value
  }

  try {
    isTableFetching.value = true
    isMetricsFetching.value = {[metric_code]: true}
    const res = await getMetricData(params)
    tableData.value = res || []

    setTimeout(() => {
      document.querySelector('.table-container')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 100)
  } catch (e) {
    console.error('Error loading metric data:', e)
  } finally {
    isTableFetching.value = false
    isMetricsFetching.value = {}
  }
}

// Watchers
watch(filters, () => {
  loadMetrics()
  if (selectedSegment.value?.status_code) {
    onChartClick(selectedSegment.value.status_code, selectedSegment.value.metric_code)
  }
  updateUrlFilters()
}, { deep: true })

watch(tableData, () => {
  columnFilters.value = {}
  dateFilters.value = {}
})

onMounted(async () => {
  loadHintState()

  // Load filters from URL on initial load
  const urlFilters = parseUrlFilters()
  if (Object.keys(urlFilters).length > 0) {
    // Apply URL filters to the filters object
    Object.keys(urlFilters).forEach(key => {
      if (filters.value.hasOwnProperty(key) && !isNaN(urlFilters[key])) {
        filters.value[key] = +urlFilters[key]
      }
    })
  }

  await loadMetrics()
})
</script>

<style scoped lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filters-card {
  background: #ffffff;
  border: 1px solid #eef0f2;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  width: fit-content;
}

.filters-container {
  display: grid;
  grid-template-columns: repeat(4, 250px);
  gap: 16px;
}

.chart-container {
  & > .charts-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    max-width: 1340px;
  }

  & > div:not(.charts-grid) {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 25px;
    row-gap: 50px;
    max-width: 900px;
  }
}

.chart-card {
  height: 340px;
  width: 320px;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 16px 0;
  padding-top: 7px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
}
.chart-header {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  margin-bottom: 8px;
  padding: 0 16px;
}

.chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2f3542;
}
.total-count {
  background: #e8eaee;
  color: #495057;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.chart-container canvas {
  cursor: pointer !important;
}

.no-data {
  color: #666;
  font-size: 16px;
  text-align: center;
  display: flex !important;
  align-items: center;
  justify-content: center;
  min-height: 260px;
}

.no-data-card {
  background: #ffffff;
  padding: 24px 28px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.no-data-icon {
  font-size: 18px;
}

.no-data-text {
  color: #5f6b7a;
}

.required-filters-hint {
  margin: 20px 0;
  margin-top: 0;
  display: flex;
  justify-content: left;
}

.hint-card {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
}

.hint-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.hint-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.hint-text {
  color: #856404;
  font-size: 14px;
  line-height: 1.5;
}

.hint-text strong {
  color: #856404;
  font-weight: 600;
}

.hint-close-btn {
  margin-left: 8px;
  flex-shrink: 0;
}

.loading-bar {
  margin-bottom: 10px;
}

.table-container {
  margin-top: 60px;
}

.table-filters {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
  align-items: flex-end;
}

.clear-filters-btn {
  align-self: flex-end;
  margin-bottom: 8px;
}

.table-filters__row {
  display: contents;
}

.clickable-id {
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.projects-section {
  margin-bottom: 32px;
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  line-height: 20px;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;
}

@media (max-width: 1200px) {
  .chart-container .charts-grid {
    grid-template-columns: repeat(2, minmax(280px, 1fr));
  }
}

@media (max-width: 900px) {
  .filters-container {
    grid-template-columns: repeat(2, 1fr);
  }
  .chart-container .charts-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .projects-grid {
    grid-template-columns: 1fr;
  }
}
</style>
